import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertCircle, CheckCircle, Loader2, Clock, XCircle, Info, User, Mail, Phone, MapPin } from 'lucide-react';
import { useAuth } from '@/features/auth';
import { supabase } from '@/lib/supabase';
import { useTranslation } from '@parentpath/i18n';

type RequestType = 'data_export' | 'data_deletion' | 'data_correction' | 'info_request';
type RequestStatus = 'pending' | 'in_progress' | 'completed' | 'rejected';

interface GDPRRequest {
  id: string;
  user_id: string;
  user_email: string;
  request_type: RequestType;
  status: RequestStatus;
  description: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
  processed_at: string | null;
}

const REQUEST_TYPES: RequestType[] = ['data_export', 'data_deletion', 'data_correction', 'info_request'];

const statusIcons: Record<RequestStatus, { icon: React.ElementType; color: string }> = {
  pending: { icon: Clock, color: 'text-pp-orange bg-pp-orange/20' },
  in_progress: { icon: Loader2, color: 'text-blue-400 bg-blue-500/20' },
  completed: { icon: CheckCircle, color: 'text-pp-teal bg-pp-teal/20' },
  rejected: { icon: XCircle, color: 'text-red-400 bg-red-500/20' },
};

export default function GDPRRequestPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { t, i18n } = useTranslation('gdpr');
  const isAuthenticated = !!user;
  const dateLocale = i18n.language === 'en' ? 'en-GB' : 'de-DE';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [selectedType, setSelectedType] = useState<RequestType | null>(null);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [existingRequests, setExistingRequests] = useState<GDPRRequest[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);

  // Guest user fields
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestAddress, setGuestAddress] = useState('');
  const [verificationInfo, setVerificationInfo] = useState('');

  // Fetch existing requests (only for authenticated users)
  useEffect(() => {
    async function fetchRequests() {
      if (!user) {
        setIsLoadingRequests(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('gdpr_requests')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setExistingRequests(data || []);
      } catch (err) {
        console.error('Error fetching GDPR requests:', err);
      } finally {
        setIsLoadingRequests(false);
      }
    }

    if (!authLoading) {
      fetchRequests();
    }
  }, [user, authLoading]);

  const handleSubmit = async () => {
    if (!selectedType) return;

    // Validate guest fields
    if (!isAuthenticated) {
      if (!guestName.trim() || !guestEmail.trim() || !verificationInfo.trim()) {
        setError(t('error.fillRequired'));
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
        setError(t('error.invalidEmail'));
        return;
      }
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const insertData = isAuthenticated
        ? {
            user_id: user!.id,
            user_email: user!.email,
            request_type: selectedType,
            description: additionalInfo || null,
            is_authenticated: true,
          }
        : {
            user_id: null,
            user_email: guestEmail,
            request_type: selectedType,
            description: additionalInfo || null,
            guest_name: guestName,
            guest_email: guestEmail,
            guest_phone: guestPhone || null,
            guest_address: guestAddress || null,
            verification_info: verificationInfo,
            is_authenticated: false,
          };

      const { error } = await supabase.from('gdpr_requests').insert(insertData);

      if (error) throw error;

      setSuccess(true);

      // Refresh requests for authenticated users
      if (isAuthenticated) {
        const { data } = await supabase
          .from('gdpr_requests')
          .select('*')
          .eq('user_id', user!.id)
          .order('created_at', { ascending: false });

        setExistingRequests(data || []);
      }
    } catch {
      setError(t('error.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedType(null);
    setAdditionalInfo('');
    setGuestName('');
    setGuestEmail('');
    setGuestPhone('');
    setGuestAddress('');
    setVerificationInfo('');
    setSuccess(false);
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-white" />
      </div>
    );
  }

  if (success) {
    return (
      <>
        <div className="glass-effect rounded-[15px] p-6">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-pp-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-pp-teal" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {t('success.title')}
            </h2>
            <p className="text-white/70 mb-4">
              {t('success.description', { email: isAuthenticated ? user?.email : guestEmail })}
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <button
                onClick={resetForm}
                className="py-2.5 px-4 glass-effect border border-white/25 text-white font-medium rounded-[15px] hover:bg-white/10 transition-colors"
              >
                {t('success.newRequest')}
              </button>
              {isAuthenticated ? (
                <Link
                  to="/profile"
                  className="py-2.5 px-4 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors"
                >
                  {t('success.toProfile')}
                </Link>
              ) : (
                <Link
                  to="/login"
                  className="py-2.5 px-4 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors"
                >
                  {t('success.toLogin')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">{t('title')}</h1>
        <p className="text-white/60 mt-1">
          {t('pageDescription')}
        </p>
      </div>

      {/* Existing Requests (only for authenticated users) */}
      {isAuthenticated && !isLoadingRequests && existingRequests.length > 0 && (
        <div className="glass-effect rounded-[15px] p-4 mb-6">
          <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5 text-white/60" />
            {t('history.title')}
          </h3>
          <div className="space-y-3">
            {existingRequests.map((request) => {
              const statusInfo = statusIcons[request.status];
              const StatusIcon = statusInfo.icon;
              return (
                <div
                  key={request.id}
                  className="flex items-center justify-between p-3 bg-white/10 rounded-[10px]"
                >
                  <div>
                    <p className="font-medium text-white">
                      {t(`requestTypes.${request.request_type}`)}
                    </p>
                    <p className="text-sm text-white/50">
                      {new Date(request.created_at).toLocaleDateString(dateLocale, {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm ${statusInfo.color}`}>
                    <StatusIcon className={`w-4 h-4 ${request.status === 'in_progress' ? 'animate-spin' : ''}`} />
                    {t(`status.${request.status}`)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Guest User Info Section */}
      {!isAuthenticated && (
        <div className="glass-effect rounded-[15px] p-4 mb-6">
          <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-white/60" />
            {t('guestForm.title')}
          </h3>
          <p className="text-sm text-white/60 mb-4">
            {t('guestForm.description')}
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('guestForm.fullName')}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  required
                  placeholder={t('guestForm.namePlaceholder')}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('guestForm.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  required
                  placeholder={t('guestForm.emailPlaceholder')}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                />
              </div>
              <p className="text-xs text-white/40 mt-1">
                {t('guestForm.emailHint')}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('guestForm.phone')}
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="tel"
                  value={guestPhone}
                  onChange={(e) => setGuestPhone(e.target.value)}
                  placeholder={t('guest.phonePlaceholder')}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('guestForm.address')}
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-white/40" />
                <textarea
                  value={guestAddress}
                  onChange={(e) => setGuestAddress(e.target.value)}
                  placeholder={t('guestForm.addressPlaceholder')}
                  rows={2}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent resize-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80 mb-1.5">
                {t('guestForm.verification')}
              </label>
              <textarea
                value={verificationInfo}
                onChange={(e) => setVerificationInfo(e.target.value)}
                required
                placeholder={t('guestForm.verificationPlaceholder')}
                rows={3}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent resize-none"
              />
              <p className="text-xs text-white/40 mt-1">
                {t('guestForm.verificationHint')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* New Request Form */}
      <div className="rounded-[15px] p-4">
        <h3 className="font-semibold text-white mb-4">
          {isAuthenticated ? t('form.title') : t('form.guestTitle')}
        </h3>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-500/20 border border-red-500/30 rounded-[15px] flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {/* Request Type Selection */}
        <div className="space-y-3 mb-6">
          <label className="block text-sm font-medium text-white/80">
            {t('form.selectType')}
          </label>
          {REQUEST_TYPES.map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`w-full text-left p-4 rounded-[15px] border-2 transition-all ${
                selectedType === type
                  ? 'border-pp-orange bg-pp-orange/20'
                  : 'border-white/20 hover:border-white/30 bg-white/5'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  selectedType === type
                    ? 'border-pp-orange bg-pp-orange'
                    : 'border-white/40'
                }`}>
                  {selectedType === type && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <div>
                  <p className="font-medium text-white">
                    {t(`requestTypes.${type}`)}
                  </p>
                  <p className="text-sm text-white/60 mt-0.5">
                    {t(`requestTypes.${type}Description`)}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Additional Info */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-white/80 mb-2">
            {t('form.additionalInfo')}
          </label>
          <textarea
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            placeholder={t('form.detailsPlaceholder')}
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-[10px] text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent min-h-[100px] resize-none"
          />
        </div>

        {/* Info Box */}
        <div className="bg-white/10 rounded-[15px] p-4 mb-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-white/50 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-white">
            <p className="font-medium">{t('info.title')}</p>
            <p className="text-white/70">{t('info.description')}</p>
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={!selectedType || isSubmitting || (!isAuthenticated && (!guestName.trim() || !guestEmail.trim() || !verificationInfo.trim()))}
          className="w-full py-3 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {t('form.submitting')}
            </>
          ) : (
            <>
              <Shield className="w-4 h-4 mr-2" />
              {t('form.submit')}
            </>
          )}
        </button>

        <p className="text-xs text-white/40 text-center mt-4">
          {t('legal.prefix')}{' '}
          <Link to="/legal/privacy" className="text-pp-orange hover:underline">
            {t('legal.privacyPolicy')}
          </Link>
          {t('legal.suffix')}
        </p>
      </div>
    </>
  );
}
